import { supabase } from '../lib/supabase';
import { retryWithBackoff } from '../utils/retryWithBackoff';

const adminService = {
  // Functions (Work Types) Management
  async getAllFunctions() {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase?.from('work_functions')?.select('*')?.order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    });
  },

  async createFunction(functionData) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase?.from('work_functions')?.insert({
          name: functionData?.name,
          description: functionData?.description,
          is_active: functionData?.is_active ?? true
        })?.select()?.single();

      if (error) throw error;
      return data;
    });
  },

  async updateFunction(functionId, updates) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase?.from('work_functions')?.update(updates)?.eq('id', functionId)?.select()?.single();

      if (error) throw error;
      return data;
    });
  },

  async deleteFunction(functionId) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { error } = await supabase?.from('work_functions')?.delete()?.eq('id', functionId);

      if (error) throw error;
    });
  },

  // Employers (Clients) Management
  async getAllEmployers() {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('role', 'client')?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    });
  },

  async createEmployer(employerData) {
    // Create auth user first
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data: authData, error: authError } = await supabase?.auth?.admin?.createUser({
          email: employerData?.email,
          password: employerData?.password,
          email_confirm: true
        });

      if (authError) throw authError;

      // Profile is created automatically via trigger, update it
      const { data, error } = await supabase?.from('user_profiles')?.update({
          full_name: employerData?.full_name,
          company_name: employerData?.company_name,
          phone: employerData?.phone,
          location: employerData?.location,
          role: 'client'
        })?.eq('id', authData?.user?.id)?.select()?.single();

      if (error) throw error;
      return data;
    });
  },

  async updateEmployer(employerId, updates) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', employerId)?.select()?.single();

      if (error) throw error;
      return data;
    });
  },

  async deleteEmployer(employerId) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { error } = await supabase?.auth?.admin?.deleteUser(employerId);

      if (error) throw error;
    });
  },

  // Skilled Professionals (Contractors) Management
  async getAllProfessionals() {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('role', 'contractor')?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    });
  },

  async createProfessional(professionalData) {
    // Create auth user first
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data: authData, error: authError } = await supabase?.auth?.admin?.createUser({
          email: professionalData?.email,
          password: professionalData?.password,
          email_confirm: true
        });

      if (authError) throw authError;

      // Profile is created automatically via trigger, update it
      const { data, error } = await supabase?.from('user_profiles')?.update({
          full_name: professionalData?.full_name,
          company_name: professionalData?.company_name,
          phone: professionalData?.phone,
          location: professionalData?.location,
          role: 'contractor'
        })?.eq('id', authData?.user?.id)?.select()?.single();

      if (error) throw error;
      return data;
    });
  },

  async deleteProfessional(professionalId) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { error } = await supabase?.auth?.admin?.deleteUser(professionalId);

      if (error) throw error;
    });
  },

  async getAllUsers(filters = {}) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      let query = supabase?.from('user_profiles')?.select('*');

      if (filters?.role) {
        query = query?.eq('role', filters?.role);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    });
  },

  async updateUserRole(userId, newRole) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase?.from('user_profiles')?.update({ role: newRole })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      return data;
    });
  },

  async deleteUser(userId) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { error } = await supabase?.auth?.admin?.deleteUser(userId);

      if (error) throw error;
    });
  },

  async getSystemStats() {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data: users, error: usersError } = await supabase?.from('user_profiles')?.select('role');

      if (usersError) throw usersError;

      const { data: jobs, error: jobsError } = await supabase?.from('jobs')?.select('status');

      if (jobsError) throw jobsError;

      return {
        totalUsers: users?.length || 0,
        contractors: users?.filter(u => u?.role === 'contractor')?.length || 0,
        clients: users?.filter(u => u?.role === 'client')?.length || 0,
        affiliates: users?.filter(u => u?.role === 'affiliate')?.length || 0,
        totalJobs: jobs?.length || 0,
        activeJobs: jobs?.filter(j => j?.status === 'open')?.length || 0
      };
    });
  }
};

export default adminService;